const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/g;
const MAX_DISCOVERIES = 8;

function boundedInteger(value, minimum, maximum) {
  if (value === null || typeof value === 'boolean' || value === '') return null;
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < minimum || parsed > maximum) return null;
  return parsed;
}

function safeTimestamp(value) {
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) return null;
  return value;
}

function safeDate(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
    ? value
    : null;
}

export function safeDisplayName(value, fallback = 'Merchant') {
  const clean = String(value ?? '')
    .replace(CONTROL_CHARACTERS, '')
    .trim();
  const bounded = Array.from(clean).slice(0, 24).join('');
  return bounded || fallback;
}

export function normalizeMarketListing(value, knownItemIds, { publicBoard = false } = {}) {
  const items = knownItemIds instanceof Set ? knownItemIds : new Set(knownItemIds || []);
  const id = typeof value?.id === 'string' && UUID_PATTERN.test(value.id) ? value.id : null;
  const itemId = typeof value?.item_id === 'string' && items.has(value.item_id) ? value.item_id : null;
  const quantity = boundedInteger(value?.quantity, 1, 999);
  const price = boundedInteger(value?.price, 1, 100000000);
  if (!id || !itemId || quantity === null || price === null) return null;
  return {
    id,
    item_id: itemId,
    quantity,
    price,
    created_at: safeTimestamp(value.created_at),
    ...(publicBoard ? { seller_name: safeDisplayName(value.seller_name) } : {}),
  };
}

export function normalizeTradeHistoryEntry(value, knownItemIds) {
  const items = knownItemIds instanceof Set ? knownItemIds : new Set(knownItemIds || []);
  const listingId = typeof value?.listing_id === 'string' && UUID_PATTERN.test(value.listing_id)
    ? value.listing_id
    : null;
  const role = value?.trade_role === 'buyer' || value?.trade_role === 'seller'
    ? value.trade_role
    : null;
  const status = value?.trade_status === 'sold' || value?.trade_status === 'cancelled'
    ? value.trade_status
    : null;
  const itemId = typeof value?.item_id === 'string' && items.has(value.item_id) ? value.item_id : null;
  const quantity = boundedInteger(value?.quantity, 1, 999);
  const price = boundedInteger(value?.price, 1, 100000000);
  const fee = boundedInteger(value?.fee, 0, price ?? 0);
  const verifiedScore = boundedInteger(value?.verified_score, 0, 500);
  if (!listingId || !role || !status || !itemId
    || quantity === null || price === null || fee === null || verifiedScore === null) return null;
  if (status === 'cancelled' && (role !== 'seller' || fee !== 0 || verifiedScore !== 0)) return null;
  if (status === 'sold' && role === 'seller' && fee < 1) return null;
  if (role === 'buyer' && verifiedScore > 250) return null;
  if (role === 'buyer' && fee !== 0) return null;
  return {
    listing_id: listingId,
    trade_role: role,
    trade_status: status,
    item_id: itemId,
    quantity,
    price,
    fee,
    verified_score: verifiedScore,
    net_gold: status === 'cancelled' ? 0 : role === 'seller' ? price - fee : -price,
    counterparty_name: status === 'sold'
      ? safeDisplayName(value.counterparty_name)
      : null,
    completed_at: safeTimestamp(value.completed_at),
  };
}

export function normalizeLeaderboardEntry(value) {
  const weeklyScore = boundedInteger(value?.weekly_score, 0, Number.MAX_SAFE_INTEGER);
  const lifetimeScore = boundedInteger(value?.lifetime_score, 0, Number.MAX_SAFE_INTEGER);
  const discoveries = boundedInteger(value?.discoveries, 0, MAX_DISCOVERIES);
  if (weeklyScore === null || lifetimeScore === null || discoveries === null) return null;
  return {
    display_name: safeDisplayName(value.display_name),
    weekly_score: weeklyScore,
    lifetime_score: lifetimeScore,
    discoveries,
    updated_at: safeTimestamp(value.updated_at),
    is_you: value.is_you === true,
  };
}

export function normalizeRosterEntry(value) {
  const socialId = typeof value?.social_id === 'string' && UUID_PATTERN.test(value.social_id)
    ? value.social_id
    : null;
  const marketScore = boundedInteger(value?.market_score, 0, Number.MAX_SAFE_INTEGER);
  const discoveries = boundedInteger(value?.discoveries, 0, MAX_DISCOVERIES);
  const lanterns = boundedInteger(value?.lanterns_this_week, 0, 1000000000);
  if (!socialId || marketScore === null || discoveries === null || lanterns === null) return null;
  return {
    social_id: socialId,
    display_name: safeDisplayName(value.display_name),
    market_score: marketScore,
    discoveries,
    updated_at: safeTimestamp(value.updated_at),
    lanterns_this_week: lanterns,
    lantern_sent_today: value.lantern_sent_today === true,
    is_you: value.is_you === true,
  };
}

export function normalizeCancellationReceipt(value, knownItemIds) {
  const items = knownItemIds instanceof Set ? knownItemIds : new Set(knownItemIds || []);
  const itemId = typeof value?.item_id === 'string' && items.has(value.item_id) ? value.item_id : null;
  const quantity = boundedInteger(value?.quantity, 1, 999);
  if (!itemId || quantity === null) return null;
  return { item_id: itemId, quantity };
}

export function normalizeLanternReceipt(value) {
  const sentOn = safeDate(value?.sent_on);
  if (!sentOn || typeof value?.recipient_name !== 'string' || !value.recipient_name.trim()) return null;
  return {
    recipient_name: safeDisplayName(value.recipient_name),
    sent_on: sentOn,
  };
}

export function normalizeCampaignStatus(value) {
  const weekStart = safeDate(value?.week_start);
  const totalPoints = boundedInteger(value?.total_points, 0, 1000000000);
  const contributors = boundedInteger(value?.contributors, 0, 100000000);
  const yourPoints = boundedInteger(value?.your_points, 0, 70);
  if (
    !weekStart
    || totalPoints === null
    || contributors === null
    || yourPoints === null
    || totalPoints % 10 !== 0
    || yourPoints % 10 !== 0
    || yourPoints > totalPoints
  ) return null;
  const claimedTiers = Array.isArray(value?.claimed_tiers)
    && value.claimed_tiers.every(tier => Number.isInteger(tier) && tier >= 1 && tier <= 3)
    ? [...new Set(value.claimed_tiers)].sort((left, right) => left - right)
    : null;
  if (
    !claimedTiers
    || (value?.contributed_today !== true && value?.contributed_today !== false)
    || (totalPoints === 0) !== (contributors === 0)
  ) return null;
  return {
    week_start: weekStart,
    total_points: totalPoints,
    contributors,
    your_points: yourPoints,
    contributed_today: value.contributed_today,
    claimed_tiers: claimedTiers,
  };
}

export function normalizeCampaignClaimReceipt(value, expectedTier) {
  const tier = boundedInteger(value?.claimed_tier, 1, 3);
  const gold = boundedInteger(value?.gold, 0, Number.MAX_SAFE_INTEGER);
  const gems = boundedInteger(value?.gems, 0, 1000000000);
  const marketScore = boundedInteger(value?.market_score, 0, Number.MAX_SAFE_INTEGER);
  if (
    tier === null
    || tier !== Number(expectedTier)
    || gold === null
    || gems === null
    || marketScore === null
  ) return null;
  return {
    gold,
    gems,
    market_score: marketScore,
    claimed_tier: tier,
  };
}

export function normalizeCloudLedgerReceipt(value, knownItemIds, { bootstrap = false } = {}) {
  const items = knownItemIds instanceof Set ? knownItemIds : new Set(knownItemIds || []);
  const gold = boundedInteger(value?.gold, 0, Number.MAX_SAFE_INTEGER);
  const gems = boundedInteger(value?.gems, 0, 1000000000);
  const marketScore = boundedInteger(value?.market_score, 0, Number.MAX_SAFE_INTEGER);
  const discoveries = boundedInteger(value?.discoveries, 0, MAX_DISCOVERIES);
  if (
    gold === null
    || gems === null
    || marketScore === null
    || discoveries === null
    || !value?.inventory
    || typeof value.inventory !== 'object'
    || Array.isArray(value.inventory)
  ) return null;
  const inventory = {};
  for (const [itemId, rawQuantity] of Object.entries(value.inventory)) {
    const quantity = boundedInteger(rawQuantity, 1, 999999);
    if (!items.has(itemId) || quantity === null) return null;
    inventory[itemId] = quantity;
  }
  if (
    bootstrap
    && (
      (value?.is_new !== true && value?.is_new !== false)
      || typeof value?.display_name !== 'string'
      || !value.display_name.trim()
    )
  ) return null;
  return {
    gold,
    gems,
    market_score: marketScore,
    discoveries,
    inventory,
    ...(bootstrap
      ? {
        display_name: safeDisplayName(value.display_name),
        is_new: value.is_new,
      }
      : {}),
  };
}

export function normalizePurchaseReceipt(value, knownItemIds) {
  const items = knownItemIds instanceof Set ? knownItemIds : new Set(knownItemIds || []);
  const itemId = typeof value?.item_id === 'string' && items.has(value.item_id) ? value.item_id : null;
  const quantity = boundedInteger(value?.quantity, 1, 999);
  const price = boundedInteger(value?.price, 1, 100000000);
  const gold = boundedInteger(value?.gold, 0, Number.MAX_SAFE_INTEGER);
  const marketScore = boundedInteger(value?.market_score, 0, Number.MAX_SAFE_INTEGER);
  const hasVerifiedScore = Object.prototype.hasOwnProperty.call(value || {}, 'verified_score');
  const verifiedScore = hasVerifiedScore
    ? boundedInteger(value.verified_score, 0, 250)
    : undefined;
  if (
    !itemId
    || quantity === null
    || price === null
    || gold === null
    || marketScore === null
    || (hasVerifiedScore && verifiedScore === null)
  ) return null;
  return {
    item_id: itemId,
    quantity,
    price,
    gold,
    market_score: marketScore,
    ...(hasVerifiedScore ? { verified_score: verifiedScore } : {}),
  };
}
